import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment';

import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import CallMissedIcon from '@mui/icons-material/CallMissed';

import Dialog from '@mui/material/Dialog';

import { GET_LIST, GET_ONE, provider, history } from '../_utils';
import { DateRangeInput, StatusChip, LoadingStub } from '../_Components';
import ShowCall from '../visor/ShowCall';
import {
    formatPhone,
    isoToSeconds,
} from '../visor/utils';
import SettingsModal from './components/SettingsModal';
import ExportModal from './components/ExportModal';
import '../visor/visor.css';
import './reports.css';

const CallTypeIcon = ({ row }) => {
    const type = String((row && row.Type) || '').toLowerCase();
    if (type.indexOf('out') >= 0) {
        return <CallMadeIcon className="pc-rpt-callico is-out" titleAccess="Исходящий" />;
    }
    if (row && row.Connected) {
        return <CallReceivedIcon className="pc-rpt-callico is-in" titleAccess="Входящий" />;
    }
    return <CallMissedIcon className="pc-rpt-callico is-miss" titleAccess="Пропущенный" />;
};

const CORE_KEYS = ['Address', 'DateConnected', 'TimeConnected'];

const secondsToHms = (value) => {
    const total = isoToSeconds(value);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

const formatCell = (header, value, row) => {
    if (value == null || value === '') return '';
    switch (header && header.type) {
        case 'datetime':
            return moment(value).isValid() ? moment(value).format('DD.MM.YYYY HH:mm:ss') : String(value);
        case 'date':
            return moment(value).isValid() ? moment(value).format('DD.MM.YYYY') : String(value);
        case 'phone':
            return formatPhone(value);
        case 'time':
            return secondsToHms(value);
        case 'success':
            return value ? 'Да' : 'Нет';
        default:
            if (header && header.value === 'TimeConnected') return secondsToHms(value);
            if (typeof value === 'boolean') return value ? 'Да' : 'Нет';
            return String(value);
    }
};

const toVisorRecord = (row) => {
    if (!row) return null;
    const outbound = String(row.Type || '').toLowerCase().indexOf('out') >= 0;
    return {
        ...row,
        id: row.id,
        uid: row.id,
        address: row.Address || row.address,
        Address: row.Address || row.address,
        operator: row.operator,
        agent: typeof row.operator === 'string' ? row.operator : (row.agent || ''),
        queue: row.queue,
        callTime: row.DateConnected,
        start: row.DateConnected,
        duration: isoToSeconds(row.TimeConnected != null ? row.TimeConnected : row.duration),
        callType: outbound ? 1 : 0,
        Type: row.Type,
        sensyId: row.sensyId,
        summary: row.summary,
        callId: row.callId,
    };
};

const ReportShow = () => {
    const { id, call } = useParams();
    const [settings, setSettings] = useState(null);
    const [headers, setHeaders] = useState([]);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [interval, setInterval] = useState({
        startDate: moment().startOf('day'),
        endDate: moment().endOf('day'),
    });
    const [phone, setPhone] = useState('');
    const [phoneDebounced, setPhoneDebounced] = useState('');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(100);
    const [openSettings, setOpenSettings] = useState(false);
    const [openExport, setOpenExport] = useState(false);
    const [callFields, setCallFields] = useState([]);
    const listRef = useRef(null);

    useEffect(() => {
        const timer = setTimeout(() => setPhoneDebounced(phone.replace(/\D/g, '')), 350);
        return () => clearTimeout(timer);
    }, [phone]);

    const loadReport = useCallback(async () => {
        const { data, status } = await provider(GET_ONE, '/reports', id);
        if (status === 200 && data) {
            setSettings(data);
            setHeaders(Array.isArray(data.headers) ? data.headers : []);
        }
    }, [id]);

    useEffect(() => {
        loadReport();
    }, [loadReport]);

    const intervalKey = [
        interval && interval.startDate && moment(interval.startDate).valueOf(),
        interval && interval.endDate && moment(interval.endDate).valueOf(),
    ].join(':');

    const filter = useMemo(() => {
        const next = {
            report: id,
            type: 'call',
            interval,
        };
        if (phoneDebounced) next.phone = phoneDebounced;
        return next;
    }, [id, interval, phoneDebounced]);

    const load = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const { status, data, total: count } = await provider(GET_LIST, '/client/callsList', {
                filter,
                sort: { field: 'time', order: 'ASC' },
                pagination: { page, perPage },
            });
            if (status === 200) {
                setRows(data || []);
                setTotal(typeof count === 'number' ? count : (data || []).length);
            } else {
                setRows([]);
                setTotal(0);
            }
        } catch (e) {
            setRows([]);
            setTotal(0);
        }
        setLoading(false);
    }, [filter, page, perPage, id]);

    useEffect(() => {
        setPage(1);
    }, [intervalKey, phoneDebounced, perPage, id]);

    useEffect(() => {
        load();
    }, [load]);

    const selected = useMemo(() => {
        if (!call || !rows.length) return null;
        return rows.find((row) => String(row.id) === String(call)) || null;
    }, [rows, call]);

    const selectedIndex = selected
        ? rows.findIndex((row) => String(row.id) === String(selected.id))
        : -1;

    useEffect(() => {
        let cancelled = false;
        const fetchCard = async () => {
            if (!selected || !selected.callId) {
                setCallFields([]);
                return;
            }
            try {
                const { data, status } = await provider(GET_ONE, '/call', selected.callId);
                if (!cancelled && status === 200 && Array.isArray(data)) {
                    setCallFields(data);
                } else if (!cancelled) {
                    setCallFields([]);
                }
            } catch (e) {
                if (!cancelled) setCallFields([]);
            }
        };
        fetchCard();
        return () => { cancelled = true; };
    }, [selected]);

    const pageCount = Math.max(1, Math.ceil((total || 0) / perPage));
    const from = total ? (page - 1) * perPage + 1 : 0;
    const to = Math.min(page * perPage, total || rows.length);
    const countLabel = total ? `${from}–${to} из ${total}` : String(rows.length);

    useEffect(() => {
        if (listRef.current) listRef.current.scrollTop = 0;
    }, [page]);

    const openRow = (row) => {
        if (!row || !row.id) return;
        if (String(call) === String(row.id)) return;
        const params = [];
        if (row.callId) params.push(`callId=${encodeURIComponent(row.callId)}`);
        if (row.sensyId) params.push(`sensyId=${encodeURIComponent(row.sensyId)}`);
        history.push(`/reports/${id}/${row.id}${params.length ? `?${params.join('&')}` : ''}`);
    };

    const closeCall = () => {
        history.push(`/reports/${id}`);
    };

    const goRel = (delta) => {
        if (selectedIndex < 0) return;
        if (delta > 0 && selectedIndex === rows.length - 1 && page < pageCount) {
            setPage(page + 1);
            return;
        }
        if (delta < 0 && selectedIndex === 0 && page > 1) {
            setPage(page - 1);
            return;
        }
        const next = rows[selectedIndex + delta];
        if (next) openRow(next);
    };

    const reportSettings = (settings && settings.settings) || {};
    const canExport = reportSettings.export !== false;
    const canListen = reportSettings.visor !== false;
    const canDownload = canListen && reportSettings.download !== false;
    const projectName = settings && settings.project && settings.project.name;
    const reportName = (settings && settings.name) || 'Отчёт';

    const cardFields = useMemo(() => {
        if (!selected) return [];
        const fromHeaders = (headers || [])
            .filter((header) => CORE_KEYS.indexOf(header.value) < 0)
            .map((header) => ({
                label: header.label,
                value: formatCell(header, selected[header.value], selected) || '—',
                wide: header.value === 'description',
            }));
        const extra = (callFields || []).filter((field) => (
            field && field.label && !fromHeaders.some((item) => item.label === field.label)
            && field.label !== 'Телефон'
        ));
        return fromHeaders.concat(extra.map((field) => ({
            label: field.label,
            value: field.value,
            wide: field.label === 'Комментарий',
        })));
    }, [selected, headers, callFields]);

    const visorRecord = toVisorRecord(selected);
    const exportFilter = {
        ...filter,
        fileName: `${projectName || 'report'}_${reportName}_${moment(interval.startDate).format('DD.MM.YYYY')}-${moment(interval.endDate).format('DD.MM.YYYY')}`,
    };

    if (!settings) {
        return (
            <div className="pc-page pc-ls pc-rpt-show">
                <LoadingStub title="Открываем отчёт" />
            </div>
        );
    }

    return (
        <div className="pc-page pc-ls pc-rpt-show">
            <div className="pc-ls-top">
                <button type="button" className="pc-ls-btn" onClick={() => history.push('/reports')}>
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" width="14" height="14">
                        <path d="M13 8H4M7.5 4.5L4 8l3.5 3.5" />
                    </svg>
                    Все отчёты
                </button>
                <h1>{projectName ? `${projectName} · ${reportName}` : reportName}</h1>
                <div className="pc-ls-dates">
                    <DateRangeInput
                        input={{ value: interval, onChange: setInterval }}
                        maxDate={new Date()}
                    />
                </div>
                <div className="pc-ls-srch">
                    <SearchIcon />
                    <input
                        className="pc-ls-inp"
                        placeholder="Телефон"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                    />
                </div>
                <StatusChip tone="mono">{countLabel}</StatusChip>
                <div className="pc-ls-top__end">
                    <button type="button" className="pc-ls-btn pc-ls-btn--ico" title="Обновить" onClick={load} disabled={loading}>
                        <RefreshIcon sx={{ fontSize: 16 }} />
                    </button>
                    {canExport && (
                        <button type="button" className="pc-ls-btn pc-ls-btn--pri" onClick={() => setOpenExport(true)} disabled={loading}>
                            <DownloadIcon sx={{ fontSize: 14 }} />
                            Экспорт в Excel
                        </button>
                    )}
                    <button type="button" className="pc-ls-btn pc-ls-btn--pri" onClick={() => setOpenSettings(true)}>
                        <SettingsIcon sx={{ fontSize: 14 }} />
                        Настройки
                    </button>
                </div>
            </div>

            <div className="pc-ls-stage pc-rpt-stage">
                <div className="pc-ls-panel">
                    <div className="pc-ls-phd">
                        <h2>Звонки</h2>
                        <StatusChip tone="mono">{countLabel}</StatusChip>
                    </div>
                    <div className="pc-ls-pbd pc-rpt-tablewrap" ref={listRef}>
                        {loading && !rows.length ? (
                            <>
                                <div className="pc-ls-skel" />
                                <div className="pc-ls-skel" />
                                <div className="pc-ls-skel" />
                            </>
                        ) : rows.length ? (
                            <table className="pc-rpt-calls">
                                <thead>
                                    <tr>
                                        {headers.map((header) => (
                                            <th key={header.value}>{header.label}</th>
                                        ))}
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => {
                                        const cur = selected && String(selected.id) === String(row.id);
                                        return (
                                            <tr
                                                key={row.id || row.callId}
                                                className={cur ? 'is-cur' : ''}
                                                onClick={() => openRow(row)}
                                            >
                                                {headers.map((header) => {
                                                    const phoneCol = header.value === 'Address' || header.type === 'phone';
                                                    const missed = row.Connected === false;
                                                    return (
                                                    <td
                                                        key={header.value}
                                                        className={header.type === 'phone' || header.type === 'datetime' || header.type === 'time' ? 'is-mono' : ''}
                                                        style={(header.value === 'TimeConnected' || phoneCol) && missed ? { color: 'var(--danger, #C8354A)' } : undefined}
                                                    >
                                                        {phoneCol ? (
                                                            <span className="pc-rpt-phone">
                                                                <CallTypeIcon row={row} />
                                                                {formatCell(header, row[header.value], row) || '—'}
                                                            </span>
                                                        ) : (formatCell(header, row[header.value], row) || '—')}
                                                    </td>
                                                    );
                                                })}
                                                <td className="pc-rpt-calls__go">
                                                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
                                                        <path d="M6 3.5L10.5 8 6 12.5" />
                                                    </svg>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        ) : (
                            <div className="pc-ls-empty">
                                <b>Ничего не найдено</b>
                                Измените интервал или фильтр по телефону.
                            </div>
                        )}
                    </div>
                    {total > 0 && (
                        <div className="pc-ls-pager">
                            <select
                                className="pc-ls-inp pc-ls-pager__size"
                                value={perPage}
                                onChange={(e) => setPerPage(Number(e.target.value))}
                            >
                                {[25, 50, 100, 200].map((n) => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </select>
                            <span className="pc-ls-pager__n">{page} / {pageCount}</span>
                            <div className="pc-ls-navbtns">
                                <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                                    <ChevronLeftIcon sx={{ fontSize: 16 }} />
                                </button>
                                <button type="button" disabled={page >= pageCount || loading} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>
                                    <ChevronRightIcon sx={{ fontSize: 16 }} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <Dialog
                open={!!visorRecord}
                onClose={closeCall}
                maxWidth={false}
                fullWidth
                className="pc-rpt-callmodal"
                sx={{
                    '& .MuiDialog-paper': {
                        width: 1080,
                        maxWidth: 'calc(100% - 32px)',
                        height: 'calc(100vh - 48px)',
                        maxHeight: 'calc(100vh - 48px)',
                        overflow: 'hidden',
                    },
                }}
            >
                {visorRecord ? (
                    <ShowCall
                        record={visorRecord}
                        onPrev={() => goRel(-1)}
                        onNext={() => goRel(1)}
                        onClose={closeCall}
                        cardFields={cardFields}
                        hidePlayer={!canListen}
                        allowDownload={canDownload}
                    />
                ) : null}
            </Dialog>

            {openSettings && (
                <SettingsModal
                    id={id}
                    showDialog={openSettings}
                    handleCloseClick={(reload) => {
                        setOpenSettings(false);
                        if (reload) {
                            loadReport().then(load);
                        }
                    }}
                />
            )}
            {openExport && (
                <ExportModal
                    showDialog={openExport}
                    handleCloseClick={() => setOpenExport(false)}
                    initialValues={exportFilter}
                    fileName={exportFilter.fileName}
                />
            )}
        </div>
    );
};

export default ReportShow;
